import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar } from 'lucide-react';
import { clsx } from 'clsx';
import { useDocumentStore } from '../../stores/documentStore';

const AssessmentDocSettings = ({ isOpen, onClose, onSaved }) => {
  const { currentDocument, enableAssessment, disableAssessment, updateAssessmentConfig } = useDocumentStore();
  const assessment = currentDocument?.assessment;

  const [timeLimitEnabled, setTimeLimitEnabled] = useState(false);
  const [timeLimit, setTimeLimit] = useState(60);
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [category, setCategory] = useState('formal');
  const [informalType, setInformalType] = useState('class-practice');
  const [showModelAnswers, setShowModelAnswers] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (assessment) {
      setCategory(assessment.category || 'formal');
      setInformalType(assessment.informalType || 'class-practice');
      setShowModelAnswers(assessment.showModelAnswers === true);
      setTimeLimitEnabled(assessment.timeLimit != null);
      setTimeLimit(assessment.timeLimit || 60);
      setValidFrom(assessment.validFrom || '');
      setValidUntil(assessment.validUntil || '');
    } else {
      setCategory('formal');
      setInformalType('class-practice');
      setShowModelAnswers(false);
      setTimeLimitEnabled(false);
      setTimeLimit(60);
      setValidFrom('');
      setValidUntil('');
    }
  }, [isOpen, assessment]);

  if (!isOpen) return null;

  const isEnabled = !!assessment?.enabled;

  const handleToggleAssessment = () => {
    if (isEnabled) {
      if (window.confirm('Disable assessment mode? Response areas will remain in the document but will not be active.')) {
        disableAssessment();
        onSaved?.();
      }
    } else {
      enableAssessment();
      onSaved?.();
    }
  };

  const handleSave = () => {
    if (!isEnabled) {
      onClose();
      return;
    }
    updateAssessmentConfig({
      category,
      informalType: category === 'informal' ? informalType : null,
      showModelAnswers,
      timeLimit: timeLimitEnabled ? timeLimit : null,
      validFrom: validFrom || null,
      validUntil: validUntil || null,
    });
    onSaved?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">Assessment Settings</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Enable / Disable toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-900">Assessment Mode</div>
              <div className="text-xs text-gray-500">Turn this document into a gradable assessment</div>
            </div>
            <button
              onClick={handleToggleAssessment}
              className={clsx(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                isEnabled ? 'bg-sigma-600' : 'bg-gray-300'
              )}
            >
              <span
                className={clsx(
                  'inline-block h-4 w-4 rounded-full bg-white transition-transform',
                  isEnabled ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </div>

          {isEnabled && (
            <>
              <div className="border-t border-gray-100 pt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                  >
                    <option value="formal">Formal</option>
                    <option value="informal">Informal</option>
                  </select>
                </div>

                {category === 'informal' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Informal Type</label>
                    <select
                      value={informalType}
                      onChange={(e) => setInformalType(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                    >
                      <option value="class-practice">Class Practice</option>
                      <option value="self-practice">Self Practice</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Self practice can be shared publicly with a link and completed like a worksheet.
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <input
                    id="assessment-show-model-answers"
                    type="checkbox"
                    checked={showModelAnswers}
                    onChange={(e) => setShowModelAnswers(e.target.checked)}
                    className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500"
                  />
                  <label htmlFor="assessment-show-model-answers" className="text-sm text-gray-700">
                    Enable model answers for self-practice responses
                  </label>
                </div>
              </div>

              {/* Time limit */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-sigma-600" />
                  <span className="text-sm font-medium text-gray-900">Time Limit</span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    id="time-limit-toggle"
                    type="checkbox"
                    checked={timeLimitEnabled}
                    onChange={(e) => setTimeLimitEnabled(e.target.checked)}
                    className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500"
                  />
                  <label htmlFor="time-limit-toggle" className="text-sm text-gray-700">Enable time limit</label>
                </div>
                {timeLimitEnabled && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="600"
                      value={timeLimit}
                      onChange={(e) => setTimeLimit(Number(e.target.value))}
                      className="w-24 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                    />
                    <span className="text-sm text-gray-500">minutes</span>
                  </div>
                )}
              </div>

              {/* Validity period */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-sigma-600" />
                  <span className="text-sm font-medium text-gray-900">Validity Period</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Available from</label>
                    <input
                      type="datetime-local"
                      value={validFrom}
                      onChange={(e) => setValidFrom(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Available until</label>
                    <input
                      type="datetime-local"
                      value={validUntil}
                      onChange={(e) => setValidUntil(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-sigma-700 text-white hover:bg-sigma-600"
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDocSettings;
